import { useKohdeSynkka } from "./Kohdekonteksti";
import { useLataus, LatausTila } from "./Lataus";
import { useCallback, useEffect, useMemo, useState } from "react";
import { naytaPvm } from "./pvm";
import { luku, luvut, prosentti } from "./luvut";
import { api, ApiError, type Etenema as EtenemaData, type EtenemaViikko, type Kohde } from "./api";
import { Ohje } from "./Ohje";
import { t, virheTeksti } from "./kieli";

/* ═══════════════════════════════════════════════════════════
   Etenemä — dia-inch-kertymä (S-käyrä), kolmella tasolla.

   Kanoninen mittari on geometria (halkaisijatuuma), ei työtunti:
   se johdetaan kirjatuista saumoista ja on siksi luotettava.

   1. VARMA VIIVA — hitsit joilla on kelvollinen päivä.
   2. KAISTA — päivätön työ jonka linjan oma aikaikkuna rajaa viikkoihin.
      Rajat, ei jakauma: alaraja = ikkuna jo päättynyt, yläraja = alkanut.
   3. KOKONAISTYÖ — vaakaviiva kaikesta hitsatusta dia-inchistä.

   Kolmas taso on se joka estää väärinlukemisen. Kaista yksin sanoisi
   "edistymä on tässä välissä", vaikka totuus on sen yläpuolella:
   ajoittamaton massa jää väliin. Kaista ilman kokonaistyön viivaa olisi
   hiljainen poissaolo uudessa asussa.
   ═══════════════════════════════════════════════════════════ */

/* Luvut jaetusta apurista — sama esitys kaikissa näkymissä. */
const num = (n: number, d = 0) => (d > 0 ? luvut(n, d) : luku(n));
const osuus = prosentti;

export default function Etenema() {
  const [kohteet, setKohteet] = useState<Kohde[]>([]);
  const [kohde, setKohde] = useState<number | "">("");
  const { ylapalkissa, synkattu } = useKohdeSynkka(kohde, setKohde, { kaikkiSallittu: true });   // T426, T472
  const [data, setData] = useState<EtenemaData | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);

  const klKohteet = useLataus();
  useEffect(() => { klKohteet.seuraa(api.kohteet().then((k) => setKohteet(k as Kohde[]))); }, [klKohteet.kerta]);

  const lataa = useCallback(async () => {
    if (!synkattu) return;   // T472: ei hakua ennen kuin kohde vastaa yläpalkkia
    try { setData(await api.etenema(kohde || undefined)); setVirhe(null); }
    catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("nippu.haku_virhe")); setData(null); }
  }, [kohde, synkattu]);
  useEffect(() => { void lataa(); }, [lataa]);

  const { viikot, tahtiH, tahtiDi } = useMemo(() => {
    const vk = data?.viikot ?? [];
    const viim = vk.slice(-4);
    const n = viim.length || 1;
    return {
      viikot: vk,
      tahtiH: viim.reduce((a, v) => a + v.hitseja, 0) / n,
      tahtiDi: viim.reduce((a, v) => a + v.dia_inch, 0) / n,
    };
  }, [data]);

  const k = data?.kattavuus;
  const kayralla = k ? k.paivatty_dia + k.kaista_dia : 0;

  return (
    <>
      <header className="head">
        <div className="head__rivi">
          <h1>{t("valikko.etenema")}</h1>
          {!ylapalkissa && <label className="dim">{t("kuittaus.kohde")}{" "}
            <select value={kohde} onChange={(e) => setKohde(e.target.value ? Number(e.target.value) : "")}>
              <option value="">{t("eten.kaikki")}</option>
              {kohteet.map((k2) => <option key={k2.id} value={k2.id}>{k2.tunnus} · {k2.nimi}</option>)}
            </select>
          </label>}
        </div>
      </header>
      <LatausTila l={klKohteet} nimi="lataus.kohteet" />
      {/* Ohje ylös (Toni 24.9.2026; Etenemä ja Puutteet menevät asiakkaalle). «Kuka» = /api/hitsausloki/etenema:
          vaadi + moduuli + kohderajaus, ei kirjoittavia toimintoja. */}
      <Ohje
        kuka={<>{t("eten.ohje.kuka")}</>}
        luvut={<>{t("eten.ohje.luvut")}</>} />
    <section className="panel">
      {virhe && <p className="huom" role="alert">{virhe}</p>}
      {data === null && !virhe && <p className="dim">{t("ltar.ladataan")}</p>}

      {data && k && (
        <>
          <div className="etenema__luvut">
            <div className="etenema__luku"><b>{num(k.hitsattu_dia, 1)}</b><span>{t("eten.di_hitsattu")}</span></div>
            {/*
              Yksi luku niputtaisi juuri sen erottelun jonka kaista tekee:
              päivätty on täsmällisesti ajoitettua, kaista rajattua. Lukija
              lukee "käyrällä" tarkoittavan edellistä, joten jälkimmäinen
              sanotaan ääneen samassa luvussa.
            */}
            <div className="etenema__luku">
              <b>{osuus(kayralla, k.hitsattu_dia)}</b>
              <span>{t("eten.kayralla")}{k.kaista_dia > 0.05
                ? t("eten.josta_kaistana", { p: osuus(k.kaista_dia, k.hitsattu_dia) })
                : ""}</span>
            </div>
            <div className="etenema__luku"><b>{num(k.hitseja)}</b><span>{t("eten.hitsia")}</span></div>
            {/*
              ⛔ KATTAVUUS ON LUVUN VIERESSA, EI ALAVIITTEESSA (Toni 29.9.2026).
              `dia_inch IS NULL` -sauma laskee nollaksi MOLEMMILLE puolille
              murtoviivaa, joten «käyrällä %» on tosi vain siitä osasta joukkoa
              jolla putkikoko on tiedossa. Mitattu jsg-pekilossa: 2 891 / 10 607
              eli 27 % hitsatuista saumoista ei ole kummassakaan luvussa.
              Rivi katoaa itsestään kun kattavuus on täysi — ja `dia_hitseja`
              puuttuessa (vanha palvelin) sitä ei piirretä lainkaan, koska
              tyhjä ei ole sama kuin täysi.
            */}
            {k.dia_puuttuu_hitseja > 0 && (
              <div className="etenema__luku">
                <b>{osuus(k.dia_hitseja, k.dia_hitseja + k.dia_puuttuu_hitseja)}</b>
                <span>{t("eten.mitattu", { n: num(k.dia_puuttuu_hitseja) })}</span>
              </div>
            )}
            <div className="etenema__luku"><b>{num(tahtiDi, 1)}</b><span>{t("eten.di_vko")}</span></div>
            <div className="etenema__luku"><b>{num(tahtiH, 1)}</b><span>{t("eten.hitsia_vko")}</span></div>
          </div>

          {/*
            Kattavuusrivi. Luvut lasketaan, ei kovakoodata — kun päiviä
            palautetaan, rivi kutistuu itsestään ja katoaa kun se on nolla.
          */}
          {k.ajoittamaton_dia > 0.05 && (
            <p className="huom" role="status">
              ⚠ <b>{t("eten.ajoittamaton", { di: num(k.ajoittamaton_dia, 1), p: osuus(k.ajoittamaton_dia, k.hitsattu_dia) })}</b> — {t("eten.ajoittamaton_jatko")}
              {k.paivaton_hitseja > 0 && <> {t("eten.paivaton", { n: k.paivaton_hitseja })}</>}
              {k.epakelpo_hitseja > 0 && <> {t("eten.epakelpo", { n: k.epakelpo_hitseja })}</>}
              {k.kaista_dia > 0.05 && <> {t("eten.kaistaan", { di: num(k.kaista_dia, 1), p: osuus(k.kaista_dia, k.hitsattu_dia) })}</>}
              {" "}{t("eten.tahti_pieni")}
            </p>
          )}

          {viikot.length > 0
            ? <SKayra viikot={viikot} kokonaistyo={k.hitsattu_dia} />
            : <p className="dim">{t("eten.ei_paivattyja")}</p>}

          {viikot.length > 0 && (
            <table className="selailu__taulu etenema__taulu">
              <thead>
                <tr><th>{t("eten.viikko")}</th><th>{t("eten.alku")}</th><th>{t("eten.hitsia_sarake")}</th><th>Dia-inch</th>
                    <th>{t("eten.kumulat")}</th><th>{t("eten.kaista")}</th></tr>
              </thead>
              <tbody>
                {[...viikot].reverse().map((v) => (
                  <tr key={v.viikko}>
                    <td className="mono">{naytaPvm(v.viikko)}</td>
                    <td>{naytaPvm(v.alku)}</td>
                    <td className="mono">{v.hitseja}</td>
                    <td className="mono">{num(v.dia_inch, 1)}</td>
                    <td className="mono">{num(v.kum, 1)}</td>
                    <td className="mono dim">
                      {v.kaista_yla - v.kaista_ala > 0.05
                        ? `${num(v.kaista_ala, 1)} – ${num(v.kaista_yla, 1)}`
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
    </>
  );
}

/* Kumulatiivinen dia-inch -käyrä. Inline SVG, ei kaaviokirjastoa. */
function SKayra({ viikot, kokonaistyo }: { viikot: EtenemaViikko[]; kokonaistyo: number }) {
  const W = 680, H = 260, P = 34;
  const n = viikot.length;
  const maxY = Math.max(...viikot.map((v) => v.kaista_yla), kokonaistyo, 1);
  // Yksi piste keskitetään, muuten jaetaan tasan leveydelle.
  const x = (i: number) => n <= 1 ? W / 2 : P + (i / (n - 1)) * (W - 2 * P);
  const y = (v: number) => H - P - (v / maxY) * (H - 2 * P);
  const pisteet = viikot.map((v, i) => `${x(i).toFixed(1)},${y(v.kum).toFixed(1)}`).join(" ");
  // Kaista: ylärajat eteenpäin, alarajat takaisin.
  const kaista = [
    ...viikot.map((v, i) => `${x(i).toFixed(1)},${y(v.kaista_yla).toFixed(1)}`),
    ...[...viikot].reverse().map((v, i) => `${x(n - 1 - i).toFixed(1)},${y(v.kaista_ala).toFixed(1)}`),
  ].join(" ");
  const onKaistaa = viikot.some((v) => v.kaista_yla - v.kaista_ala > 0.05);
  return (
    <>
    <svg className="etenema__kaavio" viewBox={`0 0 ${W} ${H}`} role="img"
         aria-label={t("eten.kaavio_aria")}>
      {/* Pinta ja viiva vaativat ≥2 pistettä; yhdellä viikolla vain piste. */}
      {n >= 2 && onKaistaa && <polygon points={kaista} className="etenema__kaista" />}
      {n >= 2 && <polyline points={pisteet} className="etenema__viiva" />}
      <line x1={P} y1={H - P} x2={W - P} y2={H - P} className="etenema__akseli" />
      <line x1={P} y1={P} x2={P} y2={H - P} className="etenema__akseli" />
      {/* Kokonaistyön viiva: KAIKKI hitsattu dia-inch, myös ajoittamaton.
          Sen ja kaistan yläreunan väli on se työ jota ei voi sijoittaa. */}
      {kokonaistyo > 0 && (
        <>
          <line x1={P} y1={y(kokonaistyo)} x2={W - P} y2={y(kokonaistyo)}
                stroke="var(--korostus, #b00021)" strokeDasharray="5 4" strokeWidth={1.5} />
          <text x={W - P} y={y(kokonaistyo) - 5} textAnchor="end" className="etenema__teksti">
            {t("eten.hitsattu_yhteensa", { n: Math.round(kokonaistyo) })}
          </text>
        </>
      )}
      {viikot.map((v, i) => (
        <circle key={v.viikko} cx={x(i)} cy={y(v.kum)} r={n === 1 ? 4 : 2.5}
                className="etenema__piste" />
      ))}
      <text x={P} y={H - P + 16} className="etenema__teksti">{naytaPvm(viikot[0]!.viikko)}</text>
      {n > 1 && (
        <text x={W - P} y={H - P + 16} className="etenema__teksti" textAnchor="end">
          {naytaPvm(viikot[n - 1]!.viikko)}
        </text>
      )}
      <text x={P + 3} y={y(maxY) + 11} className="etenema__teksti">{Math.round(maxY)} di</text>
      {onKaistaa && (
        <text x={P + 3} y={P - 8} className="etenema__teksti">
          {t("eten.kaista_selite_lyhyt")}
        </text>
      )}
    </svg>
    {/* Selite: jokainen piirretty elementti nimetään SANALLA. Kaavion
        värit ovat brändin aksenttia ja vaimennettua harmaata, joten
        väri yksin ei kerro mikä on mitattua ja mikä rajattua.
        Näytetään vain ne elementit jotka kaaviossa oikeasti on. */}
    <ul className="dim" style={{ lineHeight: 1.6, display: "flex", flexWrap: "wrap",
                                 gap: "4px 18px", margin: "-8px 0 14px", listStyle: "none", padding: 0 }}>
      {n >= 2 && (
        <li>
          <svg width={26} height={10} aria-hidden="true" style={{ verticalAlign: "middle", marginRight: 5 }}>
            <line x1={1} y1={5} x2={25} y2={5} className="etenema__viiva" />
          </svg>
          <b>{t("eten.s_viiva")}</b> = {t("eten.s_viiva_sel")}
        </li>
      )}
      <li>
        <svg width={26} height={10} aria-hidden="true" style={{ verticalAlign: "middle", marginRight: 5 }}>
          <circle cx={13} cy={5} r={2.5} className="etenema__piste" />
        </svg>
        <b>{t("eten.s_piste")}</b> = {t("eten.s_piste_sel")}
      </li>
      {n >= 2 && onKaistaa && (
        <li>
          <svg width={26} height={10} aria-hidden="true" style={{ verticalAlign: "middle", marginRight: 5 }}>
            <rect x={1} y={1} width={24} height={8} className="etenema__kaista" />
          </svg>
          <b>{t("eten.s_kaista")}</b> = {t("eten.s_kaista_sel")}
        </li>
      )}
      {kokonaistyo > 0 && (
        <li>
          <svg width={26} height={10} aria-hidden="true" style={{ verticalAlign: "middle", marginRight: 5 }}>
            <line x1={1} y1={5} x2={25} y2={5} stroke="var(--korostus, #b00021)"
                  strokeDasharray="5 4" strokeWidth={1.5} />
          </svg>
          <b>{t("eten.s_katko")}</b> = {t("eten.s_katko_sel")}
        </li>
      )}
    </ul>
    </>
  );
}
